import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '@/lib/firebase/clientApp';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, createUserWithEmailAndPassword, updateProfile, sendPasswordResetEmail, sendEmailVerification, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase/clientApp'; // Firestore reference
import { useNavigate } from 'react-router-dom';
import { continueURL } from './config';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Function to fetch all groups from Firestore
const fetchAllGroups = async () => {
  const groupsCollection = collection(db, 'groups');
  const groupsSnapshot = await getDocs(groupsCollection);

  const groupsData = groupsSnapshot.docs.map((doc) => {
    const data = doc.data();
    return { name: data.name, permissions: data.permissions || [] };
  });

  return groupsData;
};

// Function to fetch client-level defaults from Firestore
const fetchClientDefaults = async (clientId) => {
  const clientDocRef = doc(db, `clients/${clientId}`);
  const clientSnapshot = await getDoc(clientDocRef);

  if (!clientSnapshot.exists()) {
    console.error(`No client found for ID: ${clientId}`);
    return { defaultClientGroups: [], defaultClientPermissions: [] };
  }

  const clientData = clientSnapshot.data();
  const defaultClientGroups = clientData.defaultClientGroups || [];
  const defaultClientPermissions = clientData.defaultClientPermissions || [];

  return { defaultClientGroups, defaultClientPermissions };
};

// Fetch user permissions (both app and client level) from Firestore
const fetchUserPermissions = async (userId) => {
  try {
    // Fetch user document
    const userDocRef = doc(db, `users/${userId}`);
    const userSnapshot = await getDoc(userDocRef);

    if (!userSnapshot.exists()) {
      console.error(`No user found for ID: ${userId}`);
      return { appPermissions: [], appGroups: [], clientPermissions: {} };
    }

    const userData = userSnapshot.data();
    const appPermissions = userData.appPermissions || [];
    const appGroups = userData.appGroups || [];
    const profilePicture = userData.profilePicture || ''; // Add profilePicture


    // Fetch client-level permissions
    const clientPermissions = {};
    const clientsSubcollectionRef = collection(userDocRef, 'clients');
    const clientsSnapshot = await getDocs(clientsSubcollectionRef);
    // console.log(clientsSnapshot)

    // Check if the clients subcollection exists or is empty
    if (clientsSnapshot.empty) {
      console.warn(`No clients found for user ${userId}`);
      return { appPermissions, appGroups, clientPermissions }; // Return only app-level permissions
    }

    // If subcollection exists, process client documents
    for (const clientDoc of clientsSnapshot.docs) {
      const clientData = clientDoc.data();
      const { defaultClientGroups, defaultClientPermissions } = await fetchClientDefaults(clientDoc.id);

      // Combine user's client groups, default client groups, and permissions
      clientPermissions[clientDoc.id] = {
        clientName: clientData.clientName,
        clientGroups: [...new Set([...clientData.clientGroups, ...defaultClientGroups])],
        clientPermissions: [...new Set([...clientData.clientPermissions, ...defaultClientPermissions])],
        clientRole: clientData.clientRole || '',
      };
    }

    return { appPermissions, appGroups, clientPermissions, profilePicture };
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    return { appPermissions: [], clientPermissions: {} };
  }
};


export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null); // Stores custom claims, user details, and permissions
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  console.log("user: ", user)
  console.log("userDetails: ", userDetails)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        setUser(authUser);

        // Fetch the custom claims from Firebase Authentication
        const idTokenResult = await authUser.getIdTokenResult();
        const appRole = idTokenResult.claims.appRole || null;

        // Fetch all groups from Firestore
        const allGroups = await fetchAllGroups();

        // Fetch user permissions (both app and client level)
        const permissions = await fetchUserPermissions(authUser.uid);

        // Combine app groups with permissions from Firebase
        const groupPermissions = allGroups
          .filter((group) => permissions.appGroups.includes(group.name))
          .flatMap((group) => group.permissions);

        // Combine client groups' permissions with direct client permissions
        const clientPermissions = { ...permissions.clientPermissions };
        Object.keys(clientPermissions).forEach((clientId) => {
          const clientGroups = clientPermissions[clientId].clientGroups || [];
          const clientGroupPermissions = allGroups
            .filter((group) => clientGroups.includes(group.name))
            .flatMap((group) => group.permissions);

          // Combine client-specific permissions with group permissions
          clientPermissions[clientId].clientPermissions = [
            ...new Set([
              ...clientPermissions[clientId].clientPermissions,
              ...clientGroupPermissions,
            ]),
          ];
        });

        setUserDetails({
          profilePicture: permissions.profilePicture, // Set profilePicture here
          appRole,
          appPermissions: [...new Set([...permissions.appPermissions, ...groupPermissions])],
          appGroups: permissions.appGroups,
          clientPermissions, // Store both client-specific and inherited permissions
        });
      } else {
        setUser(null);
        setUserDetails(null); // Clear user details on logout
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signup = async ({ email, password }) => {
    await createUserWithEmailAndPassword(auth, email, password);
  };

  const login = async ({ email, password }) => {
    await signInWithEmailAndPassword(auth, email, password);
    navigate('/'); // Redirect to the dashboard after successful login
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null); // `userDetails` will be cleared by onAuthStateChanged
    navigate('/'); // Redirect to the homepage after logout
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email); // Firebase sends the reset email to the user's email
      console.log('Password reset email sent');
    } catch (error) {
      console.error('Error sending password reset email:', error);
    }
  };
  // Inside your AuthProvider component
  const sendVerificationEmail = async () => {
    try {
      if (auth.currentUser) {
        const actionCodeSettings = {
          url: continueURL
        }
        await sendEmailVerification(auth.currentUser, actionCodeSettings);
        console.log('Verification email sent');
      } else {
        throw new Error('No authenticated user found');
      }
    } catch (error) {
      console.error('Error sending verification email:', error);
    }
  };
  return (
    <AuthContext.Provider value={{ user, userDetails, login, logout, signup, resetPassword, sendVerificationEmail, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const updateDisplayName = async (displayName) => {
  try {
    if (auth.currentUser) {
      await updateProfile(auth.currentUser, { displayName });
      console.log('Display name updated successfully');
    } else {
      throw new Error('No authenticated user found');
    }
  } catch (error) {
    console.error('Error updating display name:', error);
  }
};
